import { activityLogEndpoint } from "@/config/endpoints";
import axiosInstance from "@/lib/axios";
import { queryClient } from "@/lib/queryClient";
import { useInfiniteQuery } from "@tanstack/react-query";

interface UseActivityLogParams {
  childId?: string | number | null;
  leadId?: string | number | null;
  category?: string;
}

export function useActivityLog({
  childId,
  leadId,
  category,
}: UseActivityLogParams) {
  const endpoint = childId
    ? `${activityLogEndpoint}/child/${childId}`
    : `${activityLogEndpoint}/lead/${leadId}`;

  const enabled = Boolean(childId || leadId);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["activity-log", childId, leadId, category],
    enabled,
    queryFn: async ({ pageParam = 1 }) => {
      const params: Record<string, any> = {
        page: pageParam,
        limit: 15,
      };
      if (category && category !== "All Activity") {
        params.category = category;
      }
      const response = await axiosInstance.get(endpoint, { params });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages) => {
      const logs = lastPage?.logs ?? [];
      if (logs.length < 15) return undefined;
      return allPages.length + 1;
    },
    select: (data) => ({
      logs: data.pages.flatMap((page: any) => page.logs || []),
    }),
  });

  return {
    logs: data?.logs ?? [],
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}

export const refetchActivityLog = () => {
  queryClient.invalidateQueries({ queryKey: ["activity-log"] });
};
